import { OpenIdConfiguration } from 'angular-auth-oidc-client';
import { environment } from '../../../environments/environment';

interface OidcEnvironmentConfig {
  authority?: string;
  clientId?: string;
  redirectUrl?: string;
  postLogoutRedirectUri?: string;
  scopes?: string;
}

const DEFAULT_AUTHORITY =
  'https://login.microsoftonline.com/ecaa386b-c8df-4ce0-ad01-740cbdb5ba55/v2.0';

// Angular navigation paths (router concerns), intentionally separate from API secure routes.
export const appRoutes = Object.freeze([
  '/',
  '/oauth/callback',
  '/view',
  '/home',
  '/search'
]);

function toAbsoluteRoute(url: string): string {
  if (!url) {
	return '';
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
	return url;
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${window.location.origin}${normalizedPath}`;
}

function toRouteVariants(url: string): string[] {
  if (!url) {
	return [];
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
	return [url];
  }

  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return [normalizedPath, toAbsoluteRoute(normalizedPath)];
}

function getApiSecureRoutes(): string[] {
  const environmentRouteSeeds = Object.entries(environment)
  .filter(([key, value]) => key.endsWith('Url') && typeof value === 'string')
  .map(([, value]) => value as string);

  const fallbackApiPaths = [
  '/download-zip',
  '/download',
  '/view',
  '/feedback',
  '/error-messages',
  '/release-notes'
  ];

  const routeSeeds = [...environmentRouteSeeds, ...fallbackApiPaths];
  return [...new Set(routeSeeds.flatMap(toRouteVariants))];
}

export function buildAuthConfig(): OpenIdConfiguration {
  const oidcConfig = (environment as { oidc?: OidcEnvironmentConfig }).oidc;
  const defaultDeniedRoute = appRoutes.includes('/') ? '/' : '/home';

  if (!oidcConfig?.clientId) {
	throw new Error('Missing OIDC clientId in environment configuration.');
  }

  const authority = oidcConfig.authority || DEFAULT_AUTHORITY;

  return {
	authority,
	clientId: oidcConfig.clientId,
	redirectUrl: oidcConfig.redirectUrl || `${window.location.origin}/oauth/callback`,
	postLogoutRedirectUri: oidcConfig.postLogoutRedirectUri || window.location.origin,
	scope: oidcConfig.scopes || 'openid profile offline_access',
	responseType: 'code',
	silentRenew: true,
	useRefreshToken: true,
	ignoreNonceAfterRefresh: true,
  unauthorizedRoute: defaultDeniedRoute,
  forbiddenRoute: defaultDeniedRoute,
	maxIdTokenIatOffsetAllowedInSeconds: 600,
  secureRoutes: getApiSecureRoutes(),
  };
}

